import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../core/context/AuthContext";
import { Logo, PrimaryButton, GhostButton, ThemeToggle } from "../../design-system";
import { typography, surfaces, borders, textColors, brandColors } from "../../design-system/tokens";
import UserProfileDropdown from "../../core/components/UserProfileDropdown";
import { buildReferralSearchParams, getReferralCoupon } from "../../core/utils/referralCoupon";

const LandingNavbar = ({ referral = null } = {}) => {
  const { auth, isHydrated } = useContext(AuthContext);
  // Prefer an explicit prop; otherwise use any coupon staged by /discount/:slug.
  const effectiveReferral = referral?.code ? referral : getReferralCoupon();
  const referralSearch = effectiveReferral?.code ? buildReferralSearchParams(effectiveReferral) : "";
  const navbarPrimaryButtonClassName = "whitespace-nowrap rounded-xl px-4 py-2 text-sm sm:text-base";

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${surfaces.glass} shadow-sm border-b ${borders.glass} transition-colors duration-300`}>
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-12 sm:h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className={`flex items-center`}>
              <Logo 
                iconSize="w-6 h-6 sm:w-8 sm:h-8" 
                textSize="text-xl sm:text-2xl" 
                useCurrentColor={false}
                iconColor="#3b82f6"
                textColor={brandColors.gradientText}
              />
            </Link>
          </div>

          {/* Right side content - conditional based on auth state */}
          <div className="flex items-center space-x-1 sm:space-x-4">
            {/* Theme Toggle - always visible, blue moon icon with blue fill on hover */}
            <ThemeToggle moonColor="text-blue-600 dark:text-blue-400" moonHoverFill="hover:fill-blue-600 dark:hover:fill-blue-400" />
            
            {!isHydrated ? (
              // Defer decision until hydration completes to avoid flash
              <div className="w-24 sm:w-40 h-9" />
            ) : auth && auth.userId ? (
              // User is logged in
              <>
                <div className={`hidden sm:block ${textColors.secondary}`}>
                  <span className={typography.bodySmall}>Welcome back,</span>
                  <span className={`ml-1 ${typography.h5} ${brandColors.primary}`}>
                    {auth.username}
                  </span>
                </div>
                <Link to={`/edit/${auth.username}`}>
                  <PrimaryButton
                    variant="gradient"
                    size="sm"
                    className={navbarPrimaryButtonClassName}
                  >
                    <span className="sm:hidden">Continue</span>
                    <span className="hidden sm:inline">Continue Creating</span>
                  </PrimaryButton>
                </Link>
                <UserProfileDropdown 
                  username={auth.username} 
                  showDashboardLink={true} 
                  showSettings={true} 
                />
              </>
            ) : (
              // User is not logged in
              <div className="flex items-center space-x-1 sm:space-x-3">
                <Link to={`/edit/login${referralSearch}`}>
                  <GhostButton
                    variant="neutral"
                    size="sm"
                    className="whitespace-nowrap sm:px-4 sm:py-2 sm:text-base"
                  >
                    Login
                  </GhostButton>
                </Link>
                <Link to={`/edit/register${referralSearch}`}>
                  <PrimaryButton
                    variant="gradient"
                    size="sm"
                    className={navbarPrimaryButtonClassName}
                  >
                    Register
                  </PrimaryButton>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default LandingNavbar;